import { useCallback, useEffect, useId, useRef, useState } from "react";
import { turnstileConfig } from "@/config/turnstileConfig";

// Cloudflare Turnstile on the anonymous onboarding forms (audit OB-06, ruling 2026-10-20):
// /apply and the invitation page's "Send me a new link". Switched on by the public site key
// VITE_TURNSTILE_SITE_KEY alone; while it is blank nothing here runs — no script, no widget,
// no token — and the forms submit exactly as they did before.
//
// The script is injected lazily, only when one of those forms mounts, exactly once per
// document (the same guard as useAcceptJs), and loaded with `render=explicit` so it renders
// nothing on its own: this hook renders the widget into the form's container. A token is
// single-use, so the form calls `reset` after a submit the API refused.

export interface TurnstileRenderOptions {
    sitekey: string;
    callback: (token: string) => void;
    "expired-callback": () => void;
    "error-callback": (errorCode: string) => void;
    "timeout-callback": () => void;
}

// The part of Cloudflare's `window.turnstile` this app uses.
export interface Turnstile {
    render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
    reset: (widgetId: string) => void;
    remove: (widgetId: string) => void;
}

declare global {
    interface Window {
        turnstile?: Turnstile;
    }
}

const SCRIPT_URL =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Marks the element we injected, so the guard below can be checked against the document.
// turnstileMock.ts hard-codes the matching selector as a literal.
const SCRIPT_MARKER = "data-turnstile";

// A download that has not finished by now is treated as failed (with Retry) rather than left
// on "Loading the security check…" forever. api.js is small and normally arrives in well under
// a second; 15 s leaves a slow mobile connection plenty of room before the form says so.
const SCRIPT_LOAD_TIMEOUT_MS = 15_000;

// One injection per document however many forms mount, StrictMode's double effect included.
// The injected element is the source of truth (the promise alone could outlive it).
let scriptReady: Promise<Turnstile> | null = null;

// Exported for its tests only.
export const loadTurnstile = (): Promise<Turnstile> => {
    const existing = document.querySelector<HTMLScriptElement>(
        `script[${SCRIPT_MARKER}]`,
    );

    if (scriptReady && existing) {
        return scriptReady;
    }

    // A module re-evaluation (Vite HMR) starts with an empty guard while the document keeps
    // what the old copy did: an api.js that already ran is reused, and one still downloading is
    // waited on below — never a second api.js.
    if (window.turnstile) {
        scriptReady = Promise.resolve(window.turnstile);
        return scriptReady;
    }

    scriptReady = new Promise<Turnstile>((resolve, reject) => {
        const script = existing ?? document.createElement("script");

        // The first outcome wins. A download abandoned by the timeout can still answer later;
        // it must not clear the guard a retry has since set. (A late success needs no check:
        // resolving a settled promise does nothing, and a load without the global goes
        // through `fail`.)
        let isSettled = false;

        // Clears the guard AND the dead element together, so Retry downloads afresh.
        const fail = (detail: string) => {
            if (isSettled) return;
            isSettled = true;
            clearTimeout(timeoutId);
            console.error(detail);
            scriptReady = null;
            script.remove();
            reject(new Error("Turnstile failed to load."));
        };

        const timeoutId = setTimeout(
            () =>
                fail(
                    `Turnstile's script did not load within ${SCRIPT_LOAD_TIMEOUT_MS}ms from "${SCRIPT_URL}" — it may be blocked or the network may be down.`,
                ),
            SCRIPT_LOAD_TIMEOUT_MS,
        );

        // api.js defines window.turnstile while it executes, so it exists by "load". A load
        // without it is some other script answering at that URL — a failure, not a wait.
        script.addEventListener("load", () => {
            if (window.turnstile) {
                isSettled = true;
                clearTimeout(timeoutId);
                resolve(window.turnstile);
                return;
            }
            fail("Turnstile's script loaded but window.turnstile is not defined.");
        });

        script.addEventListener("error", () =>
            fail(
                `Turnstile failed to load from "${SCRIPT_URL}" — the apply and new-link forms cannot be sent until it does.`,
            ),
        );

        if (!existing) {
            script.src = SCRIPT_URL;
            script.setAttribute(SCRIPT_MARKER, "");
            document.head.appendChild(script);
        }
    });

    return scriptReady;
};

export type TurnstileStatus = "loading" | "ready" | "failed";

export interface TurnstileState {
    // False while VITE_TURNSTILE_SITE_KEY is blank: the forms behave as they always have.
    isEnabled: boolean;
    status: TurnstileStatus;
    // The current single-use token, or undefined until the check passes (and after a reset,
    // an expiry or a widget error).
    token: string | undefined;
    // True while the form must not submit: Turnstile is on and there is no token yet.
    isAwaitingToken: boolean;
    // Id of the element (hint or error) explaining why the form is waiting.
    hintId: string;
    // Callback ref for the element the widget renders into.
    setContainer: (element: HTMLDivElement | null) => void;
    reset: () => void;
    retry: () => void;
}

export const useTurnstile = (): TurnstileState => {
    const { siteKey } = turnstileConfig;
    const isEnabled = Boolean(siteKey);
    const hintId = useId();

    // State, not a ref, so the widget's lifetime follows the container's: when the form gives
    // way to a confirmation, the effect's cleanup removes the widget with it.
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [status, setStatus] = useState<TurnstileStatus>("loading");
    const [token, setToken] = useState<string | undefined>(undefined);
    const [attempt, setAttempt] = useState(0);
    const widgetRef = useRef<{ turnstile: Turnstile; id: string } | null>(null);

    useEffect(() => {
        if (!isEnabled || !container) return;

        let isCurrent = true;

        loadTurnstile()
            .then((turnstile) => {
                if (!isCurrent) return;

                const id = turnstile.render(container, {
                    sitekey: siteKey,
                    // A token also ends a failure: on a retryable error Cloudflare tries
                    // again by itself (`retry` defaults to "auto"), and may then succeed.
                    callback: (value) => {
                        setToken(value);
                        setStatus("ready");
                    },
                    // The widget re-challenges by itself after both of these.
                    "expired-callback": () => setToken(undefined),
                    "timeout-callback": () => setToken(undefined),
                    // Every widget error is shown as a failure with Retry. Cloudflare's
                    // client-side error-code table (developers.cloudflare.com/turnstile/
                    // troubleshooting/client-side-errors/error-codes/) marks some codes
                    // "Retry: No" — 110100/110110 invalid or unknown sitekey, 110200 domain not
                    // authorized, 200100 clock or cache, 400020/400021/400070 sitekey invalid,
                    // wrong region or disabled — and others "Retry: Yes" (110600, 110620,
                    // 200500, 300*, 600*). But the table is partial (the `*` families' digits
                    // are internal), so the two kinds can't be told apart reliably: a
                    // persistent error must never leave a silently disabled form, and Retry
                    // (a fresh widget) is right for a retryable one too.
                    "error-callback": (errorCode) => {
                        console.error(`Turnstile widget error ${errorCode}.`);
                        setToken(undefined);
                        setStatus("failed");
                    },
                });

                if (id === undefined) {
                    console.error("Turnstile did not render a widget.");
                    setStatus("failed");
                    return;
                }

                widgetRef.current = { turnstile, id };
                setStatus("ready");
            })
            .catch(() => {
                if (isCurrent) setStatus("failed");
            });

        return () => {
            isCurrent = false;
            const widget = widgetRef.current;
            widgetRef.current = null;
            widget?.turnstile.remove(widget.id);
        };
    }, [isEnabled, siteKey, container, attempt]);

    const reset = useCallback(() => {
        setToken(undefined);
        const widget = widgetRef.current;
        widget?.turnstile.reset(widget.id);
    }, []);

    const retry = useCallback(() => {
        setStatus("loading");
        setAttempt((count) => count + 1);
    }, []);

    return {
        isEnabled,
        status,
        token,
        isAwaitingToken: isEnabled && !token,
        hintId,
        setContainer,
        reset,
        retry,
    };
};
